import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../../../services';
import { utils } from '../../../utils';

interface PreventiveMaintenanceDetail {
  id: number;
  veiculo: string;
  descricao: string;
  data_agendamento: string;
  data_execucao: string;
  custo_estimado: number;
  custo_real: number;
  fornecedor: string;
  status: string;
  prioridade: string;
  quilometragem_programada: number;
  quilometragem_atual: number;
  periodicidade: string;
  observacoes: string;
  criado_em: string;
  atualizado_em: string;
  veiculo_info?: {
    placa: string;
    modelo: string;
    marca: string;
    ano: number;
  };
  fornecedor_info?: {
    nome: string;
    cnpj: string;
    telefone: string;
    endereco: string;
  };
}

const PreventiveMaintenanceView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [maintenance, setMaintenance] = useState<PreventiveMaintenanceDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadMaintenance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadMaintenance = async () => {
    try {
      setIsLoading(true);
      setError(null);

      if (!id) {
        setError('ID não fornecido');
        return;
      }

      const response = await apiService.getPreventiveMaintenance(parseInt(id));

      if (response.success && response.data) {
        const apiData = response.data;

        const mappedMaintenance: PreventiveMaintenanceDetail = {
          id: apiData.id,
          veiculo: apiData.veiculo || apiData.veiculo_placa || 'N/A',
          descricao: apiData.descricao || '',
          data_agendamento: apiData.data_agendamento,
          data_execucao: apiData.data_execucao || '',
          custo_estimado: apiData.custo_estimado || 0,
          custo_real: apiData.custo_real || 0,
          fornecedor: apiData.fornecedor || apiData.fornecedor_nome || 'N/A',
          status: apiData.status || 'Agendada',
          prioridade: apiData.prioridade || 'Média',
          quilometragem_programada: apiData.quilometragem_programada || 0,
          quilometragem_atual: apiData.quilometragem_atual || 0,
          periodicidade: apiData.periodicidade || '',
          observacoes: apiData.observacoes || '',
          criado_em: apiData.criado_em || apiData.created_at,
          atualizado_em: apiData.atualizado_em || apiData.updated_at,
          veiculo_info: apiData.veiculo_info,
          fornecedor_info: apiData.fornecedor_info
        };

        setMaintenance(mappedMaintenance);
      } else {
        setError('Manutenção não encontrada');
        utils.showNotification('Manutenção preventiva não encontrada', { type: 'error' });
      }
    } catch (error: any) {
      console.error('Erro ao carregar manutenção preventiva:', error);
      setError('Erro ao carregar detalhes da manutenção preventiva');
      utils.showNotification(error.message || 'Erro ao carregar detalhes da manutenção preventiva', { type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const formatDateTime = (dateString: string): string => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatNumber = (value: number): string => {
    return new Intl.NumberFormat('pt-BR').format(value);
  };

  const getStatusClass = (status: string): string => {
    const statusMap: Record<string, string> = {
      'Concluída': 'status-success',
      'Em Andamento': 'status-warning',
      'Agendada': 'status-info',
      'Cancelada': 'status-danger',
      'Atrasada': 'status-danger'
    };
    return statusMap[status] || 'status-secondary';
  };

  const getStatusText = (status: string): string => {
    const statusMap: Record<string, string> = {
      'Concluída': 'Concluída',
      'Em Andamento': 'Em Andamento',
      'Agendada': 'Agendada',
      'Cancelada': 'Cancelada',
      'Atrasada': 'Atrasada'
    };
    return statusMap[status] || status;
  };

  const getPriorityClass = (prioridade: string): string => {
    const priorityMap: Record<string, string> = {
      'Urgente': 'status-danger',
      'Alta': 'status-warning',
      'Média': 'status-info',
      'Baixa': 'status-success'
    };
    return priorityMap[prioridade] || 'status-secondary';
  };

  const getPriorityText = (prioridade: string): string => {
    const priorityMap: Record<string, string> = {
      'Urgente': 'Urgente',
      'Alta': 'Alta',
      'Média': 'Média',
      'Baixa': 'Baixa'
    };
    return priorityMap[prioridade] || prioridade;
  };

  const getKmStatusClass = (programada: number, atual: number): string => {
    const diff = atual - programada;
    if (diff >= 1000) return 'status-danger'; // Atrasada
    if (diff >= 500) return 'status-warning'; // Próxima
    return 'status-success'; // No prazo
  };

  const getKmStatusText = (programada: number, atual: number): string => {
    const diff = atual - programada;
    if (diff >= 1000) return 'Atrasada';
    if (diff >= 500) return 'Próxima';
    return 'No prazo';
  };

  const calculateKmDifference = (programada: number, atual: number): number => {
    return atual - programada;
  };

  const calculateEconomy = (estimado: number, real: number): number => {
    return estimado - real;
  };

  const calculateEconomyPercentage = (estimado: number, real: number): number => {
    return estimado > 0 ? ((estimado - real) / estimado) * 100 : 0;
  };

  if (isLoading) {
    return (
      <div className="page-container">
        <div className="page-header">
          <button className="btn btn-secondary" onClick={() => navigate('/manutencoes-preventivas')}>
            <i className="fas fa-arrow-left"></i>
            Voltar
          </button>
          <h1>Carregando Manutenção...</h1>
        </div>
        <div style={{ textAlign: 'center', padding: '100px 20px' }}>
          <div className="loading-spinner-large"></div>
          <p>Carregando detalhes da manutenção preventiva...</p>
        </div>
      </div>
    );
  }

  if (error || !maintenance) {
    return (
      <div className="page-container">
        <div className="page-header">
          <button className="btn btn-secondary" onClick={() => navigate('/manutencoes-preventivas')}>
            <i className="fas fa-arrow-left"></i>
            Voltar
          </button>
          <h1>Erro ao Carregar</h1>
        </div>
        <div className="page-content">
          <div className="card">
            <div className="card-body" style={{ textAlign: 'center', padding: '50px 20px' }}>
              <i className="fas fa-exclamation-triangle" style={{ fontSize: '3rem', color: '#e74c3c', marginBottom: '20px' }}></i>
              <h3>{error || 'Manutenção não encontrada'}</h3>
              <p>A manutenção preventiva solicitada não pôde ser carregada.</p>
              <button className="btn btn-primary" onClick={() => navigate('/manutencoes-preventivas')}>
                Voltar para Lista
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const kmDifference = calculateKmDifference(maintenance.quilometragem_programada, maintenance.quilometragem_atual);
  const economy = calculateEconomy(maintenance.custo_estimado, maintenance.custo_real);
  const economyPercentage = calculateEconomyPercentage(maintenance.custo_estimado, maintenance.custo_real);

  return (
    <div className="page-container">
      <div className="page-header">
        <button className="btn btn-secondary" onClick={() => navigate('/manutencoes-preventivas')}>
          <i className="fas fa-arrow-left"></i>
          Voltar
        </button>
        <h1>Detalhes da Manutenção Preventiva</h1>
        <div className="header-actions">
          <button 
            className="btn btn-primary"
            onClick={() => navigate(`/manutencoes-preventivas/editar/${maintenance.id}`)}
          >
            <i className="fas fa-edit"></i>
            Editar
          </button>
        </div>
      </div>

      <div className="page-content">
        {/* Cabeçalho com informações principais */}
        <div className="card">
          <div className="card-header">
            <h2>Manutenção Preventiva #{maintenance.id}</h2>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <span className={`status-badge ${getStatusClass(maintenance.status)}`}>
                {getStatusText(maintenance.status)}
              </span>
              <span className={`status-badge ${getPriorityClass(maintenance.prioridade)}`}>
                {getPriorityText(maintenance.prioridade)}
              </span>
              <span className={`status-badge ${getKmStatusClass(maintenance.quilometragem_programada, maintenance.quilometragem_atual)}`}>
                {getKmStatusText(maintenance.quilometragem_programada, maintenance.quilometragem_atual)}
              </span>
            </div>
          </div>
          <div className="card-body">
            <div className="info-grid">
              <div className="info-item">
                <label>Descrição:</label>
                <span className="info-value">{maintenance.descricao}</span>
              </div>
              <div className="info-item">
                <label>Veículo:</label>
                <span className="info-value">
                  <strong>{maintenance.veiculo}</strong>
                  {maintenance.veiculo_info && (
                    <div style={{ fontSize: '0.8rem', color: '#7f8c8d' }}>
                      {maintenance.veiculo_info.marca} {maintenance.veiculo_info.modelo} ({maintenance.veiculo_info.ano})
                    </div>
                  )}
                </span>
              </div>
              <div className="info-item">
                <label>Periodicidade:</label>
                <span className="info-value">{maintenance.periodicidade}</span>
              </div>
              <div className="info-item">
                <label>KM Programada:</label>
                <span className="info-value">{formatNumber(maintenance.quilometragem_programada)} km</span>
              </div>
              <div className="info-item">
                <label>KM Atual:</label>
                <span className="info-value">{formatNumber(maintenance.quilometragem_atual)} km</span>
              </div>
              <div className="info-item">
                <label>Diferença de KM:</label>
                <span className="info-value">
                  <span className={`status-badge ${getKmStatusClass(maintenance.quilometragem_programada, maintenance.quilometragem_atual)}`}>
                    {kmDifference >= 0 ? '+' : ''}{formatNumber(kmDifference)} km
                  </span>
                </span>
              </div>
              <div className="info-item">
                <label>Status KM:</label>
                <span className="info-value">
                  <span className={`status-badge ${getKmStatusClass(maintenance.quilometragem_programada, maintenance.quilometragem_atual)}`}>
                    {getKmStatusText(maintenance.quilometragem_programada, maintenance.quilometragem_atual)}
                  </span>
                </span>
              </div>
              <div className="info-item">
                <label>Data de Agendamento:</label>
                <span className="info-value">{formatDateTime(maintenance.data_agendamento)}</span>
              </div>
              <div className="info-item">
                <label>Data de Execução:</label>
                <span className="info-value">
                  {maintenance.data_execucao ? formatDateTime(maintenance.data_execucao) : '-'}
                </span>
              </div>
              <div className="info-item">
                <label>Custo Estimado:</label>
                <span className="info-value">{formatCurrency(maintenance.custo_estimado)}</span>
              </div>
              <div className="info-item">
                <label>Custo Real:</label>
                <span className="info-value">
                  {maintenance.custo_real ? (
                    <strong>{formatCurrency(maintenance.custo_real)}</strong>
                  ) : (
                    '-'
                  )}
                </span>
              </div>
              <div className="info-item">
                <label>Fornecedor:</label>
                <span className="info-value">
                  {maintenance.fornecedor}
                  {maintenance.fornecedor_info && (
                    <div style={{ fontSize: '0.8rem', color: '#7f8c8d' }}>
                      {maintenance.fornecedor_info.telefone}
                    </div>
                  )}
                </span>
              </div>
              <div className="info-item">
                <label>Status:</label>
                <span className="info-value">
                  <span className={`status-badge ${getStatusClass(maintenance.status)}`}>
                    {getStatusText(maintenance.status)}
                  </span>
                </span>
              </div>
              <div className="info-item">
                <label>Prioridade:</label>
                <span className="info-value">
                  <span className={`status-badge ${getPriorityClass(maintenance.prioridade)}`}>
                    {getPriorityText(maintenance.prioridade)}
                  </span>
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Informações financeiras */}
        {maintenance.custo_real && (
          <div className="card" style={{ marginTop: '20px' }}>
            <div className="card-header">
              <h3>Análise Financeira</h3>
            </div>
            <div className="card-body">
              <div className="info-grid">
                <div className="info-item">
                  <label>Custo Estimado:</label>
                  <span className="info-value">{formatCurrency(maintenance.custo_estimado)}</span>
                </div>
                <div className="info-item">
                  <label>Custo Real:</label>
                  <span className="info-value">
                    <strong>{formatCurrency(maintenance.custo_real)}</strong>
                  </span>
                </div>
                <div className="info-item">
                  <label>Economia:</label>
                  <span className="info-value">
                    <strong style={{ color: economy > 0 ? '#04C64F' : '#e74c3c' }}>
                      {economy > 0 ? '+' : ''}{formatCurrency(economy)}
                    </strong>
                  </span>
                </div>
                <div className="info-item">
                  <label>Percentual de Economia:</label>
                  <span className="info-value">
                    <strong style={{ color: economy > 0 ? '#04C64F' : '#e74c3c' }}>
                      {economyPercentage.toFixed(1)}%
                    </strong>
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Informações do fornecedor */}
        {maintenance.fornecedor_info && (
          <div className="card" style={{ marginTop: '20px' }}>
            <div className="card-header">
              <h3>Informações do Fornecedor</h3>
            </div>
            <div className="card-body">
              <div className="info-grid">
                <div className="info-item">
                  <label>Nome:</label>
                  <span className="info-value">{maintenance.fornecedor_info.nome}</span>
                </div>
                <div className="info-item">
                  <label>CNPJ:</label>
                  <span className="info-value">{maintenance.fornecedor_info.cnpj}</span>
                </div>
                <div className="info-item">
                  <label>Telefone:</label>
                  <span className="info-value">{maintenance.fornecedor_info.telefone}</span>
                </div>
                <div className="info-item">
                  <label>Endereço:</label>
                  <span className="info-value">{maintenance.fornecedor_info.endereco}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Observações */}
        {maintenance.observacoes && (
          <div className="card" style={{ marginTop: '20px' }}>
            <div className="card-header">
              <h3>Observações</h3>
            </div>
            <div className="card-body">
              <p style={{ color: '#7f8c8d', lineHeight: '1.6', margin: 0 }}>
                {maintenance.observacoes}
              </p>
            </div>
          </div>
        )}

        {/* Informações de auditoria */}
        <div className="card" style={{ marginTop: '20px' }}>
          <div className="card-header">
            <h3>Informações de Auditoria</h3>
          </div>
          <div className="card-body">
            <div className="info-grid">
              <div className="info-item">
                <label>Criado em:</label>
                <span className="info-value">{formatDateTime(maintenance.criado_em)}</span>
              </div>
              <div className="info-item">
                <label>Atualizado em:</label>
                <span className="info-value">{formatDateTime(maintenance.atualizado_em)}</span>
              </div>
              <div className="info-item">
                <label>ID da Manutenção:</label>
                <span className="info-value">{maintenance.id}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Ações */}
        <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button 
            className="btn btn-secondary"
            onClick={() => navigate('/manutencoes-preventivas')}
          >
            <i className="fas fa-list"></i>
            Voltar para Lista
          </button>
          <button 
            className="btn btn-primary"
            onClick={() => navigate(`/manutencoes-preventivas/editar/${maintenance.id}`)}
          >
            <i className="fas fa-edit"></i>
            Editar Manutenção
          </button>
        </div>
      </div>
    </div>
  );
};

export default PreventiveMaintenanceView;
